import axios, { AxiosError, AxiosRequestConfig, AxiosResponse, Method } from 'axios';
import { Headers, Init, NetworkServiceError, Request, TrackCallback, TrackCategory, ExtraEventsData } from './models';
import { EventsNames } from '../../enums/Events';
import { convertEventToError } from '@sunbit-dev/purchase-common/src/enums/utils';

export class NetworkService {
  private headers: Headers = { 'Content-Type': 'application/json' };
  private baseUrl: string;
  private token: string;
  private track: TrackCallback;
  private addNetworkErrorLogs?: ({ error }: { error: NetworkServiceError }) => void;

  public networkStatusPending: { promise: Promise<void>; resolve: () => void } = {
    promise: Promise.resolve(),
    resolve: () => {},
  };

  constructor({ baseUrl, headers = {}, track, token, addNetworkErrorLogs }: Init) {
    this.headers = headers;
    this.baseUrl = baseUrl;
    this.track = track;
    this.addNetworkErrorLogs = addNetworkErrorLogs;

    if (token) this.setToken(token);

    // Always send cookies
    axios.defaults.withCredentials = this.isUsingCookies;

    // Update the monitors when the network status changes
    window.addEventListener('online', this.onNetworkStatusChange);
    window.addEventListener('offline', this.onNetworkStatusChange);
  }

  private onNetworkStatusChange = () => {
    if (navigator.onLine) {
      this.networkStatusPending.resolve();

      this.track({
        eventName: EventsNames.networkStatusOnline,
      });
    } else {
      this.track({
        eventName: EventsNames.networkStatusOffline,
      });

      this.networkStatusPending.promise = new Promise((resolve) => {
        this.networkStatusPending.resolve = resolve;
      });
    }
  };

  public setToken(token: string) {
    this.token = token;
    this.headers['Authorization'] = `Bearer ${token}`;
  }

  get isUsingCookies(): boolean {
    return false;
  }

  public getToken = () => {
    return this.token;
  };

  public removeToken() {
    this.token = undefined;
    this.headers['Authorization'] = undefined;
  }

  private getRequestPublicConfig = ({ config }: { config: AxiosRequestConfig }): ExtraEventsData => {
    const { url, method, baseURL, headers, params, timeout, responseType } = config || {};

    return {
      url,
      method: method as Method,
      baseURL,
      headers,
      params,
      timeout,
      responseType,
    };
  };

  public async request<T>(request: Request): Promise<T> {
    const { method = 'get', url, headers = {}, trackingInfo, data, params, retries = 2 } = request;

    try {
      const response: AxiosResponse = await axios({
        method,
        url,
        headers: { ...this.headers, ...headers },
        data,
        baseURL: this.baseUrl,
        params,
      });

      if (trackingInfo)
        this.track({
          eventName: trackingInfo.eventName,
          props: { ...trackingInfo.props, params, headers, url, baseUrl: this.baseUrl, method },
        });

      return response.data;
    } catch (error: any) {
      const eventName = trackingInfo?.eventName
        ? convertEventToError(trackingInfo?.eventName)
        : EventsNames.unknownNetworkError;

      const publicConfig = this.getRequestPublicConfig({ config: error?.config });

      const errorInfo = {
        eventName,
        ...publicConfig,
        code: error?.response?.data?.code,
        responseData: {
          ...error?.response?.data,
        },
        status: error?.response?.status,
      } as NetworkServiceError;

      this.addNetworkErrorLogs?.({ error: errorInfo });

      const errorInfoWithCode = { code: (error as AxiosError<{ code: string }>)?.response?.data.code };

      // In case of 500, 502, 503, 504, 0 errors, retry the request
      if ([500, 502, 503, 504, 0, undefined].includes(error.response?.status) && retries > 0) {
        this.track({
          eventName,
          props: {
            error: errorInfo,
          },
          datadogInfo: {
            type: TrackCategory.Action,
            errorInfo: errorInfoWithCode,
          },
        });

        return await this.request({ ...request, retries: retries - 1 });
      } else {
        this.track({
          eventName,
          props: {
            error: errorInfo,
          },
          datadogInfo: {
            type: TrackCategory.Error,
            errorInfo: errorInfoWithCode,
          },
        });

        throw errorInfo;
      }
    }
  }

  public addHeader = (name: string, value: string) => {
    if (!name) {
      console.log('Cannot add empty header');
      return;
    }

    this.headers[name] = value;
  };

  public removeHeader = (name: string) => {
    delete this.headers[name];
  };
}
